import React from "react";
import { StatusCard } from "../components";
import { FindFlightById, getPassengerCounts } from "../helpers";

export const FlightInfoBox = ({
  flight,
  passengerList = [],
  refreshSeatmapData,
  isLoading,
  bagList = {},
  showRotateButton = false,
  onRotateSeatMap = () => {},
  isSeatMapRotated = false,
}) => {
  const { flightId, flightDate = "", flightNumber = "", allFlights } = flight;
  const selectedFlight = FindFlightById(allFlights, flightId);


  // Filter out extra seats (CBBG and EXST)
  const actualPassengers = passengerList.filter(
    (p) => p.passengerType !== "B" && p.passengerType !== "E"
  );

  const totalPassengers = actualPassengers.length;
  const {
    countAdultsCheckedIn,
    countInfantsCheckedIn,
    countAdultsBoarded,
    countInfantsBoarded,
    countInfants,
  } = getPassengerCounts(actualPassengers);

  const passengerCounts = getPassengerCounts(actualPassengers);

  const checkedIn = countAdultsCheckedIn + countInfantsCheckedIn;

  const bagLookup = React.useMemo(() => {
    const byPassengerId = new Map();
    const byBookingRef = new Map();
    const byOwnerRefId = new Map();
    const bags = bagList?.bags;

    const normalizeRefId = (ref) => {
      if (typeof ref !== "string") return null;
      const parts = ref.split("/");
      return parts[parts.length - 1] || ref;
    };

    if (!Array.isArray(bags)) {
      return { byPassengerId, byBookingRef, byOwnerRefId, normalizeRefId };
    }

    const addToMap = (map, key, bag) => {
      if (!key) return;
      const current = map.get(key) || {
        bags: [],
        checkedInCount: 0,
        loadedCount: 0,
      };
      current.bags.push(bag);
      if (bag.checkedIn) current.checkedInCount += 1;
      if (bag.loaded) current.loadedCount += 1;
      map.set(key, current);
    };

    for (const bag of bags) {
      const normalizedBag = {
        id: bag?.id ?? null,
        tagNumber: bag?.licensePlate ?? bag?.tagNumber ?? null,
        licensePlate: bag?.licensePlate ?? null,
        loadingStatus: bag?.loadingStatus ?? null,
        activationStatus: bag?.activationStatus ?? null,
        finalDestination: bag?.finalDestination ?? null,
        checkedIn:
          typeof bag?.checkedIn === "boolean"
            ? bag.checkedIn
            : bag?.activationStatus === "ACTIVE",
        loaded:
          typeof bag?.loaded === "boolean"
            ? bag.loaded
            : bag?.loadingStatus === "LOADED",
        ownerId: bag?.ownerId ?? null,
        ownerRef: bag?.ownerRef ?? null,
        ownerRefId: bag?.ownerRefId ?? normalizeRefId(bag?.ownerRef),
        ownerRecordLocator: bag?.ownerRecordLocator ?? null,
      };

      addToMap(byPassengerId, normalizedBag.ownerId, normalizedBag);
      addToMap(byOwnerRefId, normalizedBag.ownerRefId, normalizedBag);
      addToMap(byBookingRef, normalizedBag.ownerRecordLocator, normalizedBag);
    }

    return { byPassengerId, byBookingRef, byOwnerRefId, normalizeRefId };
  }, [bagList]);

  const getPassengerBagInfo = React.useCallback(
    (passenger) => {
      if (!passenger) return null;

      const passengerId = passenger.id || null;
      const passengerIdNormalized = bagLookup.normalizeRefId
        ? bagLookup.normalizeRefId(passengerId)
        : passengerId;

      const byId = bagLookup.byPassengerId.get(passengerId);
      const byOwnerRef = passengerIdNormalized
        ? bagLookup.byOwnerRefId.get(passengerIdNormalized)
        : null;
      const bookingRef =
        passenger.customerBookingReference || passenger.recordLocator || null;
      const byBookingRef = bookingRef
        ? bagLookup.byBookingRef.get(bookingRef)
        : null;

      const match = byId || byOwnerRef || byBookingRef;

      if (!match) return null;

      return {
        ...match,
        source: byId ? "passenger" : byOwnerRef ? "ref" : "pnr",
      };
    },
    [bagLookup]
  );

  const awaitedPassengers = React.useMemo(
    () => actualPassengers.filter((p) => !p.boarded),
    [actualPassengers]
  );

  const awaitedPassengersBagsCheckedIn = React.useMemo(() => {
    if (!Array.isArray(awaitedPassengers) || awaitedPassengers.length === 0) {
      return 0;
    }

    return awaitedPassengers.reduce((acc, passenger) => {
      const bagInfo = getPassengerBagInfo(passenger);
      return acc + (bagInfo?.checkedInCount || 0);
    }, 0);
  }, [awaitedPassengers, getPassengerBagInfo]);

  const totalBoarded = countAdultsBoarded + countInfantsBoarded;
  const totalCheckedIn = countAdultsCheckedIn + countInfantsCheckedIn;

  const isBoardingComplete =
    totalBoarded === totalCheckedIn && totalCheckedIn > 0;

  const awaitedBagsLabel =
    isBoardingComplete || awaitedPassengers.length === 0
      ? null
      : awaitedPassengersBagsCheckedIn > 0
      ? `${awaitedPassengersBagsCheckedIn}`
      : "No luggage for awaited passengers";

  const safeRefreshSeatmapData =
    refreshSeatmapData && selectedFlight ? refreshSeatmapData : undefined;

  return (
    <StatusCard
      flightId={flightId}
      date={flightDate}
      flightNumber={flightNumber}
      airport={selectedFlight?.sDep[0]}
      boardPoint={selectedFlight?.sDep[0]}
      offPoint={selectedFlight?.sDest[0]}
      checkInInfo={`${checkedIn} of ${totalPassengers}`}
      checkInCompleted={checkedIn === totalPassengers}
      boardingInfo={`${countAdultsBoarded} of ${totalPassengers}`}
      totalPassengers={totalPassengers}
      refreshSeatmapData={safeRefreshSeatmapData}
      isLoading={isLoading}
      passengerCounts={passengerCounts}
      bagList={bagList}
      showRotateButton={showRotateButton}
      onRotateSeatMap={onRotateSeatMap}
      isSeatMapRotated={isSeatMapRotated}
      awaitedBagsLabel={awaitedBagsLabel}
      totalInfants={countInfants}
    />
  );
};
